import { useEffect, useState } from "react";
import DemandFilter from "../../components/Filter/demandForecastFilter";
import { FilterConfig, IDemandPayload, IRoot } from "../../services/interface/IDemand";
import FeatureCard from "../../components/FeatureCard";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "rsuite";
import { setDBFilter, setSelectedDBFilter } from "../../services/redux/reducer/demandfilter.reducer";
import { useFeatureImportanceFilterDropdown, useFeatureImportanceLineChart, useFeatureImportanceTableData } from "../../services/redux/hooks/useFeatureImportance";
import { IDemandVSForecast } from "../../services/interface/IDemandForecast";
import { IRoot as IForecastRoot} from "../../services/interface/IHome";
import moment from "moment";
import CustomLoader from "../../common/CustomLoader/CustomLoader";

const FeatureImportance = () => {
    const dispatch = useDispatch();
    const initialState: IDemandPayload = {
        dc: [],
        storeNumber: [],
        filterItemId: [],
        fcwNumber: ["3"],
        ucode: [],
        productSegm: [],
        productDesc: [],
        category: [],
        subCategory: [],
        vendorName: [],
        modelType: ["error based best model"],
    }
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedFilterData, setSelectedFilterData] = useState<IDemandPayload>(initialState);
    const [filter, setFilter] = useState<IDemandPayload>(initialState);
    const [demandForecastData, setDemandForecastData] = useState<IDemandVSForecast[]>();
    const [accuracyData, setAccuracyData] = useState();
    const [featureImportanceTable, setFeatureImportanceTable] = useState();

    const applyFilter = useSelector((state:IRoot)=>state.dbfilter.applyFilter);
    const selectedDbFilter = useSelector((state:IRoot)=>state.dbfilter.selectedDbFilter);
    const lastUpdated = useSelector((state:IForecastRoot)=>state.filter.lastUpdateDate) || "";
    const {mutate: mutateDropDown, data: resFilterData} = useFeatureImportanceFilterDropdown();
    const {mutate: mutateLineChart } = useFeatureImportanceLineChart();
    const {mutate: mutateTableData } = useFeatureImportanceTableData();

    //Filter - Start
    const filters: FilterConfig[] = [
        { title: 'DC', key: 'dc', placeholder: 'All' },
        { title: 'Item Id', key: 'filterItemId', placeholder: 'All' },
        { title: 'Forecast Week', key: 'fcwNumber', placeholder: 'All', isSelectPicker: true },    
    ]; 

    const filterHandler = async () => {
        setLoading(true);
        try {
            await mutateDropDown(selectedFilterData);
        }
        catch (err) {
            console.error(err);
        }
        finally {
            setLoading(false);
        }
    }

    const setFilterData=()=>{
        if(resFilterData?.data){
            setFilter(resFilterData?.data);
            dispatch(setDBFilter(resFilterData?.data));
        }
    }

    const filterSelectionHandler = (key: any, value: any) => {
        setSelectedFilterData({
            ...selectedFilterData,
            [key]: value
        });
    }

    const removeSelectionHandler = () => {
        setSelectedFilterData(initialState);
    }
    // Filter - End

    const prepareDemandForecast = (data: any) => {
        if (data) {
            let finalData: IDemandVSForecast[] = [];
            Array.isArray(data) &&
                data.map((each: IDemandVSForecast, index: number) => {
                    // Check if the forecastDate is before "2023-11-22T00:00:00.000Z"
                    const forecastDate = moment(each?.forecastDate);
                    const cutoffDate = moment("2023-11-22T00:00:00.000Z");
                    let obj = {
                        actuals: each?.actuals,
                        forecastDate: moment(each?.forecastDate?.slice(0, 10)).format("MM-DD-YY"),
                        totalForecast: forecastDate.isBefore(cutoffDate) ? null : each?.totalForecast,
                    };
                    finalData.push(obj);
                });
            setDemandForecastData(finalData);
        }
    };

    const getHistoricalData=async(payload: IDemandPayload)=>{
        try{
            setLoading(true);
            const res = await mutateLineChart(payload);
            if(res?.data){
                prepareDemandForecast(res?.data?.data?.lineChartData ?? []);
                setAccuracyData(res?.data?.data?.accuracy)
                // setLastUpdated(res?.data?.data?.lastUpdateDate);
            }
        }
        catch(err){
            console.error(err);
        }
        finally{
            setLoading(false);
        }   
    }

    const sortQuarterData = (data: { qrt1Data: any[]; qrt2Data: any[]; qrt3Data: any[]; qrt4Data: any[]; }) => {
        let localData:any = []
        const parsePercentage = (str: string) => parseFloat(str.replace('%', ''));
      
        // Extract and copy data for each quarter
        const qrt1Data = [...data?.qrt1Data];
        const qrt2Data = [...data?.qrt2Data];
        const qrt3Data = [...data?.qrt3Data];
        const qrt4Data = [...data?.qrt4Data];
      
        // Sort each quarter's data in descending order based on QtrX_Shap
        qrt1Data.sort((a, b) => parsePercentage(b?.Qtr1_Shap) - parsePercentage(a?.Qtr1_Shap));
        qrt2Data.sort((a, b) => parsePercentage(b?.Qtr2_Shap) - parsePercentage(a?.Qtr2_Shap));
        qrt3Data.sort((a, b) => parsePercentage(b?.Qtr3_Shap) - parsePercentage(a?.Qtr3_Shap));
        qrt4Data.sort((a, b) => parsePercentage(b?.Qtr4_Shap) - parsePercentage(a?.Qtr4_Shap));
      
        // Update the original data structure with the sorted data
        localData["qrt1Data"] = qrt1Data;
        localData["qrt2Data"] = qrt2Data;
        localData["qrt3Data"] = qrt3Data;
        localData["qrt4Data"] = qrt4Data;
      
        return localData;
    };      

    const getTabularData=async(payload: IDemandPayload)=>{
        try{
            setLoading(true);
            const res = await mutateTableData(payload);
            if(res?.data){
                const sortedData = sortQuarterData(res?.data?.data);
                setFeatureImportanceTable(sortedData);
                // setFeatureImportanceTable(res?.data?.data);
            }
        }
        catch(err){
            console.error(err);
        }
        finally{
            setLoading(false);
        }  
    }

    useEffect(()=>{
        dispatch(setSelectedDBFilter(selectedFilterData));
    },[selectedFilterData]);

    useEffect(() => {
        setFilterData();
        return () => {
            dispatch(setSelectedDBFilter(initialState));
        }
    }, [resFilterData?.data]);

    useEffect(()=>{
        if(applyFilter === ""){
            getHistoricalData(initialState);
            getTabularData(initialState);
        }
        else if(applyFilter === "applied"){
            getHistoricalData(selectedDbFilter);
            getTabularData(selectedDbFilter);
        }
    },[applyFilter, dispatch]);
    
    return <>
        <div className="relative">
        <DemandFilter
            selectedFilterData={selectedFilterData}
            filterSelectionHandler={filterSelectionHandler}
            removeSelectionHandler={removeSelectionHandler}
            resFilterData={filter}
            filters={filters}
            filterHandler={filterHandler}
            alertDesc="Demand Forecasting > Feature Importance" 
            lastUpdated={lastUpdated} />
        </div>
        <div className="bg-[#F1F1F1] h-[70vh] px-4 py-3 grow flex flex-col">
            <FeatureCard 
                title={"Trend of Historical Demand"}
                dataName={["Actuals","Total Forecast"]}
                dataKey={["actuals","totalForecast"]}
                accuracyData={accuracyData}
                tableData={featureImportanceTable}
                data={demandForecastData ?? []}
            />
        </div>

        {loading && <CustomLoader open={loading} />}
    </>
}

export default FeatureImportance;